import { useState } from 'react'

import { useTailgateGrid } from '../../hooks/useTailgateGrid'
import { useIncidentSearch } from '../hooks/useIncidentSearch'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { Box, Flex, HStack, Tbody, Td, Text, Tr } from '@chakra-ui/react'

import {
  Button,
  SearchInput,
  Table,
  TableFooter,
  TableHeader,
  TableSpinner,
} from '@/components/ui'
import { IncidentType } from '@/graphql/generated/schemas'

import { IncidentOptionI } from '../../types/types'
import {
  TYPE_FILTER_OPTIONS,
  incidentTableColumns,
} from '../constants/incidentTable'

interface IncidentSearchPanelIProps {
  selectedIncident: IncidentOptionI | null
  setSelectedIncident: (option: IncidentOptionI | null) => void
  /**
   * When provided, renders a "Use ${displayId}" button above the filters so
   * the user can switch back to the preset incident without re-selecting it
   * from the table.
   */
  presetIncident?: IncidentOptionI | null
  onUsePreset?: () => void
}

export const IncidentSearchPanel = ({
  selectedIncident,
  setSelectedIncident,
  presetIncident,
  onUsePreset,
}: IncidentSearchPanelIProps) => {
  const { associateDeviceValues } = useTailgateGrid()
  const [typeFilter, setTypeFilter] = useState<IncidentType | null>(null)
  const {
    incidents,
    isLoading,
    searchTerm,
    setSearchTerm,
    totalCount,
    pageIndex,
    pageSize,
    pageCount,
    goToPage,
    nextPage,
    previousPage,
  } = useIncidentSearch(associateDeviceValues?.doorId, typeFilter)

  const table = useReactTable({
    data: incidents,
    columns: incidentTableColumns,
    getCoreRowModel: getCoreRowModel(),
    meta: { selectedId: selectedIncident?.id },
  })

  return (
    <>
      {presetIncident && onUsePreset && (
        <Button
          _hover={{ bg: 'purple.100' }}
          bg='purple.50'
          border='1px solid'
          borderColor='purple.200'
          color='purple.700'
          mb={3}
          onClick={onUsePreset}
          size='sm'
          variant='ghost'
        >
          Use {presetIncident.incidentId}
        </Button>
      )}
      <HStack spacing={2}>
        {TYPE_FILTER_OPTIONS.map((option) => (
          <Button
            key={option.label}
            onClick={() => {
              setTypeFilter(option.value)
              goToPage(0)
            }}
            size='sm'
            variant={typeFilter === option.value ? 'primary' : 'outline'}
          >
            {option.label}
          </Button>
        ))}
      </HStack>
      <Box mt={3}>
        <SearchInput
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder='Search by Incident ID'
          value={searchTerm}
        />
      </Box>
      <Box mt={4}>
        {isLoading ? (
          <TableSpinner height='200px' />
        ) : incidents.length === 0 ? (
          <Flex align='center' h='200px' justify='center'>
            <Text color='gray.500' fontSize='sm'>
              No incidents with video clips found for this door
            </Text>
          </Flex>
        ) : (
          <Table
            tableContainerStyles={{
              overflowX: 'hidden',
              borderRadius: 'xl',
            }}
            tableStyles={{ sx: { tableLayout: 'fixed' } }}
          >
            <TableHeader px={2} table={table} />
            <Tbody>
              {table.getRowModel().rows.map((row) => (
                <Tr
                  _hover={{ bgColor: 'gray.100' }}
                  bg={
                    row.original.id === selectedIncident?.id
                      ? 'blue.50'
                      : undefined
                  }
                  cursor='pointer'
                  data-testid={`row-${row.original.incidentId}`}
                  h='32px'
                  key={row.id}
                  onClick={() => setSelectedIncident(row.original)}
                  transition='ease-in-out'
                  transitionDuration='300ms'
                  transitionProperty='all'
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      color='#838692'
                      fontSize='11px'
                      fontWeight={500}
                      key={cell.id}
                      px={2}
                      py={1}
                      w={cell.column.getSize()}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
              {/* Pad with blank rows to maintain constant table height when fewer than 10 results */}
              {Array.from({
                length: pageSize - table.getRowModel().rows.length,
              }).map((_, i) => (
                <Tr borderBottom='none' h='32px' key={`blank-${i}`}>
                  <Td
                    borderBottom='none'
                    colSpan={incidentTableColumns.length}
                  />
                </Tr>
              ))}
            </Tbody>
            {pageCount >= 1 && (
              <TableFooter
                onGoToPage={goToPage}
                onNextPage={nextPage}
                onPreviousPage={previousPage}
                pageCount={pageCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                showBorder={false}
                total={totalCount}
              />
            )}
          </Table>
        )}
      </Box>
    </>
  )
}
